import { useEffect, useMemo, useRef, useState } from 'react'

import { ApiError } from '~/api/client'
import { useAuth } from '~/auth/auth-context'
import type { AiEntry } from '~/api/endpoints'
import { Working } from '~/components/working'
import { useWork } from '~/components/use-work'
import { DeleteIcon } from '~/components/icons'
import { MealPage } from '~/components/meal-page'
import { EMPTY_MEAL_STATE, type Entry, type GoalOnDay, type Meal } from '~/data/nutrition'
import { CountedTextarea } from '~/components/counted-textarea'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useApi } from '~/data/data-context'
import { recipeNameFrom } from '~/data/nutrition'
import type { AiState } from '~/lib/ai-state'
import { downscaleToJpeg } from '~/lib/downscale-image'

/** How much the description and the further details each take, and no more. */

const PRIMARY =
  'rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50'

const QUIET = 'rounded-full px-4 py-2 text-sm text-accent transition-colors hover:bg-hover'

/** What the server said no with, said as the panel says it. */
function refusal(error: unknown): string {
  if (error instanceof ApiError && error.status === 402) {
    const said = (error.body as { error?: string } | null)?.error
    return said === 'plan-has-no-ai'
      ? 'Your plan does not include the AI.'
      : "You have used this month's AI allowance."
  }
  if (error instanceof ApiError && error.status === 503) {
    return 'The AI is not set up on this server.'
  }
  return 'The AI could not be reached. Try again in a moment.'
}

/**
 * Asking the AI what a meal is — the nutrition spec, Asking the AI. One step to ask on, with a
 * photo or a description or both, and one to review what came back on: the entries it made,
 * each of which can be taken out or opened as a quick entry, and further details it can be
 * asked again with. What is logged is every entry left, at the day and time the dialog opened on.
 */
export function AskAiPanel({
  state,
  onState,
  day,
  time,
  entries = [],
  goal = null,
  onClose,
  onLogged,
  onKept,
}: {
  state: AiState
  onState: (next: AiState) => void
  /** The day and time anything logged from the answer lands on. */
  day: string
  time: string
  /** The day's entries and goal, for what the answer would leave of the day. */
  entries?: readonly Entry[]
  goal?: GoalOnDay | null
  /** Putting the answer away with nothing logged. */
  onClose: () => void
  /** The answer logged: logging is over, and whatever is behind this goes with it. */
  onLogged: () => void
  /** The answer kept as a meal of the account's own, which the page is then — the spec's Meals. */
  onKept?: ((meal: Meal) => void) | undefined
}) {
  const api = useApi()
  const { plan } = useAuth()
  const [asking, setAsking] = useState(false)
  const [askingAgain, setAskingAgain] = useState(false)
  const [reason, setReason] = useState<string | null>(null)
  const picking = useWork()
  const [detailing, setDetailing] = useState(false)
  const detailField = useRef<HTMLTextAreaElement>(null)
  /** Counts the answers, so the page reading the answer as a meal tells one from the next. */
  const [answered, setAnswered] = useState(0)

  /**
   * Why the AI cannot be asked at all, where it cannot — the plan allowing none, or the
   * month's allowance being spent. Asking is still offered: a way that vanished would leave
   * nowhere for the reason to be said.
   */
  const barred =
    plan === null
      ? null
      : plan.aiTokensPerMonth === 0
        ? 'Your plan does not include the AI.'
        : plan.aiTokensUsed >= plan.aiTokensPerMonth
          ? "You have used this month's AI allowance."
          : null

  // The details take the cursor as they come out, and give it back as they go away.
  useEffect(() => {
    if (detailing) {
      detailField.current?.focus()
    }
  }, [detailing])

  const ask = async (previous: readonly AiEntry[] | null, details: string | null) => {
    // Asking again keeps the answer on the screen; only the first ask takes it over.
    const again = previous !== null
    if (again) {
      setAskingAgain(true)
    } else {
      setAsking(true)
    }
    setReason(null)
    try {
      // Straight to the server, never through the offline queue: an answer asked for now is
      // worth nothing sent hours later, so with no connection this fails and is asked again.
      const answer = await api.askAi({
        image: state.image,
        description: state.description.trim() === '' ? null : state.description.trim(),
        previous,
        details,
      })
      if (answer.entries.length === 0) {
        onState({ ...state, answer: null, details: '' })
        setReason(answer.note ?? 'The AI found no food in that.')
      } else {
        // Details that have been asked with are put away and forgotten once the answer is back.
        setDetailing(false)
        setAnswered((held) => held + 1)
        onState({
          ...state,
          answer: answer.entries,
          name: answer.name,
          details: '',
        })
      }
    } catch (error) {
      onState({ ...state, answer: null })
      setReason(refusal(error))
    } finally {
      setAsking(false)
      setAskingAgain(false)
    }
  }

  const answer = useMemo(() => state.answer ?? [], [state.answer])
  /*
   * The answer as a meal — the nutrition spec, Asking the AI. It is one: a collection of foods
   * and the amounts of them, one slot apiece in the order the entries came, named and marked
   * as the AI said, with no food behind any of them and never kept. So it is read on the
   * meal's own page rather than on a second page saying the same things in another hand. A
   * new meal each time the AI answers, which the page takes as a step of its own — and the
   * same meal between answers, so the page keeps what has been done to it across a render.
   */
  const marks = useMemo(
    () => new Map(answer.map((each, at) => [`ai-${String(answered)}-${String(at)}`, each.emoji])),
    [answer, answered],
  )
  const asAMeal: Meal = useMemo(
    () => ({
      id: `answer-${String(answered)}`,
      name: state.name ?? recipeNameFrom(answer.map((each) => each.name)),
      emoji: null,
      slots: answer.map((each, at) => ({
        id: `ai-${String(answered)}-${String(at)}`,
        alternatives: [
          {
            id: `ai-${String(answered)}-${String(at)}a`,
            member: { name: each.name, food: null, amount: each.amount, nutrients: each.nutrients },
          },
        ],
      })),
      state: EMPTY_MEAL_STATE,
      members: answer.map((each) => ({
        name: each.name,
        food: null,
        amount: each.amount,
        nutrients: each.nutrients,
      })),
      logs: [],
      logCount: 0,
      lastLoggedAt: null,
    }),
    [answer, answered, state.name],
  )

  if (asking) {
    /*
     * The shape of the page the answer will be read on — what it is headed by and the rows it
     * will hold — rather than a mark turning in the middle of an empty panel. The answer then
     * settles into room that was already being held, instead of shoving a line off the screen.
     */
    return (
      <SkeletonBlock label="Asking the AI" className="space-y-3">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-64" />
        <div className="space-y-2 pt-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
        <p className="pt-1 text-sm text-ink-muted">Working out what this is…</p>
      </SkeletonBlock>
    )
  }

  if (state.answer === null) {
    const canSend = barred === null && (state.image !== null || state.description.trim() !== '')
    return (
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (canSend) {
            void ask(null, null)
          }
        }}
      >
        {(reason ?? barred) !== null && (
          <p role="alert" className="text-sm text-ink-muted">
            {reason ?? barred}
          </p>
        )}
        <div className="flex items-start gap-3">
          {state.image === null ? (
            <label className={`${QUIET} relative cursor-pointer ring-1 ring-white/10`}>
              {/* Shrinking the photo on the device is work to wait on, said on the control. */}
              <Working shown={picking.shown}>Add a photo</Working>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                aria-label="Photo"
                aria-busy={picking.working || undefined}
                disabled={picking.working}
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (file === undefined) {
                    return
                  }
                  void picking.run(async () => {
                    try {
                      const image = await downscaleToJpeg(file)
                      onState({ ...state, image })
                    } catch {
                      setReason('That photo could not be read.')
                    }
                  })
                }}
              />
            </label>
          ) : (
            <div className="relative">
              <img
                src={`data:image/jpeg;base64,${state.image}`}
                alt="What was picked"
                className="size-24 rounded-2xl object-cover"
              />
              <button
                type="button"
                aria-label="Remove photo"
                className="absolute -top-2 -right-2 rounded-full bg-surface-raised p-1 ring-1 ring-white/10"
                onClick={() => {
                  onState({ ...state, image: null })
                }}
              >
                <DeleteIcon aria-hidden className="size-4" />
              </button>
            </div>
          )}
          <CountedTextarea
            label="Description"
            placeholder="What was it? “Two eggs on toast with butter.”"
            rows={3}
            focusOnMount
            value={state.description}
            onValue={(description) => {
              onState({ ...state, description })
            }}
          />
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={!canSend} className={PRIMARY}>
            Ask
          </button>
        </div>
      </form>
    )
  }

  return (
    <MealPage
      meal={asAMeal}
      exists={false}
      day={day}
      entries={entries}
      goal={goal}
      time={time}
      onClose={onClose}
      onLogged={onLogged}
      onKept={onKept}
      asking={{
        busy: askingAgain,
        details: state.details,
        detailing,
        onDetails: (details) => {
          onState({ ...state, details })
        },
        onDetailing: setDetailing,
        detailsRef: detailField,
        onAskAgain: (standing) => {
          void ask(
            standing.map(({ slotId, member }) => ({
              name: member.name,
              emoji: marks.get(slotId) ?? '',
              amount: member.amount,
              nutrients: member.nutrients,
            })),
            state.details.trim(),
          )
        },
      }}
    />
  )
}
